import { firstValueFrom } from 'rxjs';
import {
  Input,
  OnInit,
  Output,
  Component,
  OnChanges,
  EventEmitter,
  SimpleChanges,
  ComponentFactoryResolver
} from '@angular/core';
import get from 'lodash/get';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

import {
  ngHelper,
  dataHelper,
  ApiService,
  currencyParser,
  CustomComponent,
  IPrintingPicture,
  SentryLoggerService,
  CieUtilsModalManagerService
} from '@cakeiteasy/web-utils';
import { ImageCropperExtraService, UiChooseMotiveModalComponent } from '@cakeiteasy/web-ui';

@Component({
  standalone: false,
  selector: 'cake-motive-section',
  templateUrl: './cake-motive-section.component.html',
  styleUrls: ['./cake-motive-section.component.scss']
})
export class CakeMotiveSectionComponent extends CustomComponent implements OnChanges, OnInit {
  @Input() printingPicture: IPrintingPicture;
  @Input() activePrinting: IPrintingPicture;
  @Input() disabled: boolean;
  @Input() disabledChooseMotiveModal: boolean;
  @Input() active: boolean;
  @Input() currency = 'kr';

  @Output() onSelect = new EventEmitter<IPrintingPicture>();
  @Output() onRemove = new EventEmitter<void>();

  public isCropping = false;
  public imageUploading: Promise<any>;
  public options: any[];
  public activePrintingUrl: string;
  public isRequired = false;
  public title: string;

  public optionControl: FormControl;

  constructor(private _api: ApiService,
              private _extraCropper: ImageCropperExtraService,
              private _translator: TranslateService,
              private _logger: SentryLoggerService,
              private _modalService: CieUtilsModalManagerService,
              private _componentResolver: ComponentFactoryResolver) {
    super();
  }

  ngOnInit(): void {
    this.init();
  }

  ngOnChanges(changes: SimpleChanges) {
    ngHelper.ifChanged(changes, 'printingPicture', () => {
      this.init();
    });

    ngHelper.ifChanged(changes, 'activePrinting', (activePrinting) => {
      this.activePrintingUrl = get(activePrinting, 'url');
      this.init();
    });
  }

  init() {
    this.options = this._prepareOptions(this.printingPicture, this.currency);
    this.isRequired = this.printingPicture.requiredForCustomer;
    this.title = this.printingPicture.customQuestionText || this.printingPicture.questionText;

    this.optionControl = new FormControl(this.activePrinting && this.activePrinting.id);
    this.addSubscriber(
      this.optionControl.valueChanges
        .subscribe(value => this.selectOption(value))
    );

    if (this.activePrinting) {
      if (!this._isChosen(this.activePrinting.id)) {
        this.selectNo();
      } else {
        this.selectOption(this.activePrinting.id);
      }
    } else {
      setTimeout(() => this.selectNo(), 0);
    }
  }

  public uploadAction(url?: string) {
    return this.disabledChooseMotiveModal ? this.isCropping = true
      : this.showMotiveModal(url);
  }

  selectNo() {
    let noOption = this.options.find(o => o.id === 0);
    this.activePrinting = noOption as IPrintingPicture;
    this.optionControl.setValue(0, { emitEvent: false });
    this.onSelect.emit(noOption);
  }

  _prepareOptions(printingPicture, currency) {
    const priceSuffix = printingPicture.price > 0 ? ` - ${currencyParser(printingPicture.price, currency)}` : '';
    const printingOptions = [{
      name: `${this._translator.instant('common.yes')}${priceSuffix}`,
      id: printingPicture.id
    }];

    const newOptions = [
      {
        name: this._translator.instant('common.no'),
        id: 0
      }].concat(printingOptions);

    return dataHelper.leftJoin(this.options, newOptions, 'id').reverse();
  }

  public selectOption(id?: number) {
    if (id) {
      let url = get(this.activePrinting, 'url');
      this.activePrinting = this.printingPicture;
      this.activePrinting.url = url;

      if (!url) {
        if (this.disabledChooseMotiveModal) {
          this.isCropping = true;
        } else {
          this.showMotiveModal();
        }
      } else {
        this.onSelect.emit(this.activePrinting);
      }
    } else {
      this.selectNo();
    }
  }

  onImageCropped(imgSrc: string) {
    // $segment.trackAttempToAddMotive($scope.orderLine);
    this.isCropping = false;
    return this.save(imgSrc)
      .then((url?: string) => {
        this.activePrinting.url = url;
        this.onSelect.emit(this.activePrinting);
        return url;
      })
      .catch((err) => {
        if (!this.activePrinting || !this.activePrinting.url) {
          this.cancel();
          this.selectNo();
        }
      });
  }

  showMotiveModal(url?: string) {
    // $segment.trackAttempToAddMotive($scope.orderLine);
    return this._modalService
      .show(UiChooseMotiveModalComponent, this._componentResolver,
        { imgUrl: url }, { backdrop: 'static' })
      .then((imgSrc) => {
        return this.save(imgSrc);
      })
      .then((url?: string) => {
        this.activePrinting.url = url;
        this.onSelect.emit(this.activePrinting);
        return url;
      })
      .catch(() => {
        if (!this.activePrinting || !this.activePrinting.url) {
          this.cancel();
          this.selectNo();
        }
      });
  }

  cancel() {
    this.onRemove.emit();
  }

  public chooseNew() {
    if (this.disabledChooseMotiveModal) {
      this.activePrintingUrl = '';
      this.isCropping = true;
    } else {
      this.showMotiveModal();
    }
  }

  save(imgSrc: string): Promise<string> {
    if (!imgSrc) {
      this.selectNo();
      // $scope.orderLine.motiveFormat = eMotiveFormats.none;
      return;
    }

    // if base64
    const base64Str = ';base64,';
    if (imgSrc.indexOf(base64Str) === -1) {
      this.activePrinting.url = imgSrc;
      this.onSelect.emit(this.activePrinting);

      if (imgSrc.indexOf('http') === 0) {
        return Promise.resolve(imgSrc);
      }

      return Promise.resolve('');
    }


    return this.imageUploading = firstValueFrom(this._api
      .post('api.extra', 'images', null, {
        image: imgSrc,
        fileName: Date.now() + '-' + Math.random() + '.jpg'
      }))
      .then((uploaded) => {
        this._extraCropper.checkIfImageIsEmpty(imgSrc, 'Motive Section', uploaded.image.originalUrl);

        return uploaded.image.originalUrl;
      })
      .catch(() => {
        return null;
      });
  }

  private _isChosen(id) {
    return this.printingPicture.id == id;
  }
}


